
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import {
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    Field,
    FormBreadcrumbs,
    GroupedField,
    GroupedFields,
    GroupedTable,
    GroupedTableRow,
    ICrfConditionParameters,
    IGroupedTableCellProps,
    IGroupedTableHeaderProps,
    Input,
    InstitutionContext,
    MasterGroupContext,
    Numeric,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { FunctionComponent, useContext } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { Box, List, ListItem, styled, Typography } from '@mui/material';
import { ConditionMode, ValidateOn } from '@ngt/forms-core';
import * as Dtos from '../../../api/dtos';
import { AppSettings } from '../../../AppSettings';
import DateAndCheckboxField from '../fields/DateAndCheckboxField';
import SelectWithTooltip from '../inputs/SelectWithTooltip';

/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface ISurgeryFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */
const StyledSubheading = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3, 2, 3, 2)
    
}))


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const indexLesionBreastConvervingSurgeryNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.indexLesionBreastConvervingSurgeryNotDone;
const indexLesionBcsMarginReExcision1NotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.indexLesionBcsMarginReExcision1NotDone;
const indexLesionBcsMarginReExcision2NotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.indexLesionBcsMarginReExcision2NotDone;
const indexLesionUnilateralMastectomyNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.indexLesionUnilateralMastectomyNotDone;
const indexLesionBilateralMastectomyNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.indexLesionBilateralMastectomyNotDone;

const malignantLesionBreastConvervingSurgerySameExcisionNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.malignantLesionBreastConvervingSurgerySameExcisionNotDone;
const malignantLesionBreastConvervingSurgerySeparateExcisionNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.malignantLesionBreastConvervingSurgerySeparateExcisionNotDone;
const malignantLesionBreastConvervingSurgeryContralateralNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.malignantLesionBreastConvervingSurgeryContralateralNotDone;
const malignantLesionBcsMarginReExcision1NotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.malignantLesionBcsMarginReExcision1NotDone;
const malignantLesionBcsMarginReExcision2NotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.malignantLesionBcsMarginReExcision2NotDone;
const malignantLesionBcsMarginReExcision1ContralateralNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.malignantLesionBcsMarginReExcision1ContralateralNotDone;
const malignantLesionBcsMarginReExcision2ContralateralNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.malignantLesionBcsMarginReExcision2ContralateralNotDone;
const malignantLesionUnilateralMastectomyNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.malignantLesionUnilateralMastectomyNotDone;
const malignantLesionBilateralMastectomyNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.malignantLesionBilateralMastectomyNotDone;

const sentinelLymphNodeBiopsyNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.sentinelLymphNodeBiopsyNotDone;
const axillaryDissectionNotDone = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => !formState?.value?.axillaryDissectionNotDone;

const malignantLesionSurgeryPerformed = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => formState?.value?.malignantLesionSurgeryPerformed === Dtos.LookupYesNoType.Yes;

const sentinelHasPositive = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => (formState?.value?.numberSentinelNodesPositive ?? 0) > 0;
const axillaryHasPositive = ({ formState }: ICrfConditionParameters<Dtos.Surgery>) => (formState?.value?.numberAuxillaryNodesPositive ?? 0) > 0;

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */
const sentinelNodeBiopsyHeaders: IGroupedTableHeaderProps[] = [
    {
        columnName: 'numberRemoved',
        content: 'Number of sentinel nodes removed?',
        TableCellProps: {
            sx: {
                width: '16%'
                //minWidth: 300
            }
        }
    },
    {
        columnName: 'numberPositive',
        content: 'Number of positive?',
        TableCellProps: {
            sx: {
                width: '14%',
                //background: 'rgba(86, 44, 133, 0.1)'
                //minWidth: 200
            }
        }
    },
    // {
    //     columnName: 'numberPositiveItcs',
    //     content: 'Number of ITCs?',
    //     TableCellProps: {
    //         sx: {
    //             width: '14%',
    //             background: 'rgba(86, 44, 133, 0.1)'
    //             //minWidth: 200
    //         }
    //     }
    // },
    // {
    //     columnName: 'numberPositiveMicrometastases',
    //     content: 'Number of Micrometastases?',
    //     TableCellProps: {
    //         sx: {
    //             width: '14%',
    //             background: 'rgba(86, 44, 133, 0.1)'
    //             //minWidth: 200
    //         }
    //     }
    // },
    // {
    //     columnName: 'numberPositiveMetastases',
    //     content: 'Number of Metastases?',
    //     TableCellProps: {
    //         sx: {
    //             width: '14%',
    //             background: 'rgba(86, 44, 133, 0.1)'
    //             //minWidth: 200
    //         }
    //     }
    // },
    {
        columnName: 'numberNegative',
        content: 'Number of negative?',
        TableCellProps: {
            sx: {
                width: '14%',
                //minWidth: 200
            }
        }
    },
    {
        columnName: 'pathology',
        content: 'Lymph node pathology',
        TableCellProps: {
            sx: {
                width: '14%',
                //minWidth: 200
            }
        }
    }
];

const sentinelNodeBiopsyCells: IGroupedTableCellProps[] = [
    {
        columnName: 'numberRemoved',
        content: (
            <Input
                component={Numeric}
            />
        ),
        propertyPath: "numberSentinelNodesRemoved",
    },
    {
        columnName: 'numberPositive',
        content: (
            <>
                <Input
                    component={Numeric}
                />
                <CrfCondition
                    mode={ConditionMode.Show}
                    condition={sentinelHasPositive}
                    subscription={{ value: true }}
                >
                    <Field propertyPath={'numberSentinelNodesPositiveItcs'} prependParentPropertyPath={false}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginTop: '1rem'
                            }}
                        >
                            <Typography variant="body1">
                                Number of ITCs
                            </Typography>
                            <Box
                                sx={{
                                    flex: '1 1 auto'
                                }}
                            >
                                <Input 
                                    component={Numeric}
                                    placeholder=''
                                />
                            </Box>
                        </Box>
                    </Field>
                    <Field propertyPath={'numberSentinelNodesPositiveMicrometastases'} prependParentPropertyPath={false}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginTop: '1rem'
                            }}
                        >
                            <Typography variant="body1">
                                Number of Micrometastases
                            </Typography>
                            <Box
                                sx={{
                                    flex: '1 1 auto'
                                }}
                            >
                                <Input 
                                    component={Numeric}
                                />
                            </Box>
                        </Box>
                    </Field>
                    <Field propertyPath={'numberSentinelNodesPositiveMetastases'} prependParentPropertyPath={false}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginTop: '1rem'
                            }}
                        >
                            <Typography variant="body1">
                                Number of Metastases
                            </Typography>
                            <Box
                                sx={{
                                    flex: '1 1 auto'
                                }}
                            >
                                <Input 
                                    component={Numeric}
                                />
                            </Box>
                        </Box>
                    </Field>
                </CrfCondition>
            </>
        ),
        propertyPath: "numberSentinelNodesPositive"
    },
    // {
    //     columnName: 'numberPositiveItcs',
    //     content: (
    //         <CrfCondition
    //             mode={ConditionMode.Enable}
    //             condition={sentinelHasPositive}
    //             subscription={{ value: true }}
    //         >
    //             <Input
    //                 component={Numeric}
    //                 style={{ background: 'rgba(86, 44, 133, 0.1)' }}
    //             />
    //         </CrfCondition>
    //     ),
    //     propertyPath: "numberSentinelNodesPositiveItcs"
    // },
    // {
    //     columnName: 'numberPositiveMicrometastases',
    //     content: (
    //         <CrfCondition
    //             mode={ConditionMode.Enable}
    //             condition={sentinelHasPositive}
    //             subscription={{ value: true }}
    //         >
    //             <Input
    //                 component={Numeric}
    //                 style={{ background: 'rgba(86, 44, 133, 0.1)' }}
    //             />
    //         </CrfCondition>
    //     ),
    //     propertyPath: "numberSentinelNodesPositiveMicrometastases"
    // },
    // {
    //     columnName: 'numberPositiveMetastases',
    //     content: (
    //         <CrfCondition
    //             mode={ConditionMode.Enable}
    //             condition={sentinelHasPositive}
    //             subscription={{ value: true }}
    //         >
    //             <Input
    //                 component={Numeric}
    //                 style={{ background: 'rgba(86, 44, 133, 0.1)' }}
    //             />
    //         </CrfCondition>
    //     ),
    //     propertyPath: "numberSentinelNodesPositiveMetastases"
    // },
    {
        columnName: 'numberNegative',
        content: (
            <Input
                component={Numeric}
            />
        ),
        propertyPath: "numberSentinelNodesNegative"
    },
    {
        columnName: 'pathology',
        content: (
            <Input
                component={SelectWithTooltip}
                tooltipProps={{
                    arrow: true,
                    placement: "right",
                    title: <>
                        <List>
                            <ListItem>N0 &mdash; No regional lymph node metastasis identified or ITCs onlyss</ListItem>
                            <ListItem>N0(i+) &mdash; ITCs only (malignant cell clusters no larger than 0.2mm) in regional lymph node(s)</ListItem>
                            <ListItem>N1mi &mdash; Micrometastases (approximately 200 cells, larger than 0.2mm, but none larger than 2.0mm)</ListItem>
                            <ListItem>N1a &mdash; Metastases in 1-3 axillary lymph nodes, at least one metastasis larger than 2.0mm</ListItem>
                            <ListItem>N1 &mdash; Micrometastases; or metastases in 1-3 axillary lymph nodes; and/or clinically negative internal mammary nodes with micrometastases or macrometastases by sentinel lymph node biopsy</ListItem>
                            <ListItem>N2 &mdash; Metastases in 4-9 axillary lymph nodes</ListItem>
                            <ListItem>N3 &mdash; Metastases in 10 or more axillary lymph nodes</ListItem>
                        </List>
                    </>
                }}
            />
        ),
        propertyPath: "lymphNodePathology"
    },
];

const axillaryNodeDissectionHeaders: IGroupedTableHeaderProps[] = [
    {
        columnName: 'numberRemoved',
        content: 'Number of nodes removed?',
        TableCellProps: {
            sx: {
                width: '16%',
                //minWidth: 300
            }
        }
    },
    {
        columnName: 'numberPositive',
        content: 'Number of positive?',
        TableCellProps: {
            sx: {
                width: '14%',
                //minWidth: 200
            }
        }
    },
    // {
    //     columnName: 'numberPositiveItcs',
    //     content: 'Number of ITCs?',
    //     TableCellProps: {
    //         sx: {
    //             width: '14%',
    //             //minWidth: 200
    //         }
    //     }
    // },
    // {
    //     columnName: 'numberPositiveMicrometastases',
    //     content: 'Number of Micrometastases?',
    //     TableCellProps: {
    //         sx: {
    //             width: '14%',
    //             //minWidth: 200
    //         }
    //     }
    // },
    // {
    //     columnName: 'numberPositiveMetastases',
    //     content: 'Number of Metastases?',
    //     TableCellProps: {
    //         sx: {
    //             width: '14%',
    //             //minWidth: 200
    //         }
    //     }
    // },
    {
        columnName: 'numberNegative',
        content: 'Number of negative?',
        TableCellProps: {
            sx: {
                width: '14%',
                //minWidth: 200
            }
        }
    },
    {
        columnName: 'pathology',
        content: 'Lymph node pathology ',
        TableCellProps: {
            sx: {
                width: '14%',
                //minWidth: 200
            }
        }
    }
];

const axillaryNodeDissectionCells: IGroupedTableCellProps[] = [
    {
        columnName: 'numberRemoved',
        content: (
            <Input
                component={Numeric}
            />
        ),
        propertyPath: "numberAuxillaryNodesRemoved"
    },
    {
        columnName: 'numberPositive',
        content: (
            <>
                <Input
                    component={Numeric}
                />
                <CrfCondition
                    mode={ConditionMode.Show}
                    condition={axillaryHasPositive}
                    subscription={{ value: true }}
                >
                    <Field propertyPath={'numberAuxillaryNodesPositiveItcs'} prependParentPropertyPath={false}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginTop: '1rem'
                            }}
                        >
                            <Typography variant="body1">
                                Number of ITCs
                            </Typography>
                            <Box
                                sx={{
                                    flex: '1 1 auto'
                                }}
                            >
                                <Input 
                                    component={Numeric}
                                    placeholder=''
                                />
                            </Box>
                        </Box>
                    </Field>
                    <Field propertyPath={'numberAuxillaryNodesPositiveMicrometastases'} prependParentPropertyPath={false}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginTop: '1rem'
                            }}
                        >
                            <Typography variant="body1">
                                Number of Micrometastases
                            </Typography>
                            <Box
                                sx={{
                                    flex: '1 1 auto'
                                }}
                            >
                                <Input 
                                    component={Numeric}
                                />
                            </Box>
                        </Box>
                    </Field>
                    <Field propertyPath={'numberAuxillaryNodesPositiveMetastases'} prependParentPropertyPath={false}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginTop: '1rem'
                            }}
                        >
                            <Typography variant="body1">
                                Number of Metastases
                            </Typography>
                            <Box
                                sx={{
                                    flex: '1 1 auto'
                                }}
                            >
                                <Input 
                                    component={Numeric}
                                />
                            </Box>
                        </Box>
                    </Field>
                </CrfCondition>
            </>
        ),
        propertyPath: "numberAuxillaryNodesPositive"
    },
    // {
    //     columnName: 'numberPositiveItcs',
    //     content: (
    //         <CrfCondition
    //             mode={ConditionMode.Enable}
    //             condition={axillaryHasPositive}
    //             subscription={{ value: true }}
    //         >
    //             <Input
    //                 component={Numeric}
    //             />
    //         </CrfCondition>
    //     ),
    //     propertyPath: "numberAuxillaryNodesPositiveItcs"
    // },
    // {
    //     columnName: 'numberPositiveMicrometastases',
    //     content: (
    //         <CrfCondition
    //             mode={ConditionMode.Enable}
    //             condition={axillaryHasPositive}
    //             subscription={{ value: true }}
    //         >
    //             <Input
    //                 component={Numeric}
    //             />
    //         </CrfCondition>
    //     ),
    //     propertyPath: "numberAuxillaryNodesPositiveMicrometastases"
    // },
    // {
    //     columnName: 'numberPositiveMetastases',
    //     content: (
    //         <CrfCondition
    //             mode={ConditionMode.Enable}
    //             condition={axillaryHasPositive}
    //             subscription={{ value: true }}
    //         >
    //             <Input
    //                 component={Numeric}
    //             />
    //         </CrfCondition>
    //     ),
    //     propertyPath: "numberAuxillaryNodesPositiveMetastases"
    // },
    {
        columnName: 'numberNegative',
        content: (
            <Input
                component={Numeric}
            />
        ),
        propertyPath: "numberAuxillaryNodesNegative"
    },
    {
        columnName: 'pathology',
        content: (
            <Input
                component={SelectWithTooltip}
                tooltipProps={{
                    arrow: true,
                    placement: "right",
                    title: <>
                        <List>
                            <ListItem>N0 &mdash; No regional lymph node metastasis identified or ITCs onlyss</ListItem>
                            <ListItem>N0(i+) &mdash; ITCs only (malignant cell clusters no larger than 0.2mm) in regional lymph node(s)</ListItem>
                            <ListItem>N1mi &mdash; Micrometastases (approximately 200 cells, larger than 0.2mm, but none larger than 2.0mm)</ListItem>
                            <ListItem>N1a &mdash; Metastases in 1-3 axillary lymph nodes, at least one metastasis larger than 2.0mm</ListItem>
                            <ListItem>N1 &mdash; Micrometastases; or metastases in 1-3 axillary lymph nodes; and/or clinically negative internal mammary nodes with micrometastases or macrometastases by sentinel lymph node biopsy</ListItem>
                            <ListItem>N2 &mdash; Metastases in 4-9 axillary lymph nodes</ListItem>
                            <ListItem>N3 &mdash; Metastases in 10 or more axillary lymph nodes</ListItem>
                        </List>
                    </>
                }}
            />
        ),
        propertyPath: "auxillaryNodesPathology"
    },
];


const SurgeryForm: FunctionComponent<ISurgeryFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings.debug}
            >
                <StyledSubheading
                    variant="h2"
                >
                    Surgical management – Index Lesion
                </StyledSubheading>
                <StyledSubheading>
                    Which surgeries were performed on the index lesion? Please complete for all surgeries performed.
                </StyledSubheading>
                <DateAndCheckboxField 
                    label={
                        <Typography variant="body1">
                            Breast conserving surgery
                        </Typography>
                    }
                    condition={indexLesionBreastConvervingSurgeryNotDone}
                    datePropertyPath="indexLesionBreastConvervingSurgeryDate"
                    notDonePropertyPath="indexLesionBreastConvervingSurgeryNotDone"
                />
                <DateAndCheckboxField 
                    label={
                        <Typography variant="body1">
                            BCS margin re-excision 1
                        </Typography>
                    }
                    condition={indexLesionBcsMarginReExcision1NotDone}
                    datePropertyPath="indexLesionBcsMarginReExcision1Date"
                    notDonePropertyPath="indexLesionBcsMarginReExcision1NotDone"
                />
                <DateAndCheckboxField 
                    label={
                        <Typography variant="body1">
                            BCS margin re-excision 2
                        </Typography>
                    }
                    condition={indexLesionBcsMarginReExcision2NotDone}
                    datePropertyPath="indexLesionBcsMarginReExcision2Date"
                    notDonePropertyPath="indexLesionBcsMarginReExcision2NotDone"
                />
                <DateAndCheckboxField 
                    label={
                        <Typography variant="body1">
                            Unilateral Mastectomy
                        </Typography>
                    }
                    condition={indexLesionUnilateralMastectomyNotDone}
                    datePropertyPath="indexLesionUnilateralMastectomyDate"
                    notDonePropertyPath="indexLesionUnilateralMastectomyNotDone"
                />
                <DateAndCheckboxField 
                    label={
                        <Typography variant="body1">
                            Bilateral Mastectomy
                        </Typography>
                    }
                    condition={indexLesionBilateralMastectomyNotDone}
                    datePropertyPath="indexLesionBilateralMastectomyDate"
                    notDonePropertyPath="indexLesionBilateralMastectomyNotDone"
                />

                <StyledSubheading
                    variant="h2"
                >
                    Surgical management – Malignant Occult Lesion/s
                </StyledSubheading>
                <GroupedField 
                    component={Select}
                    propertyPath="malignantLesionSurgeryPerformed"
                />
                <CrfCondition
                    mode={ConditionMode.Show}
                    condition={malignantLesionSurgeryPerformed}
                    subscription={{ value: true }}
                >
                    <StyledSubheading>
                        Which surgeries were performed on the malignant occult lesion? Please complete for all surgeries performed.
                    </StyledSubheading>

                    <DateAndCheckboxField 
                        label={
                            <Typography variant="body1">
                                Breast conserving surgery same excision as index
                            </Typography>
                        }
                        condition={malignantLesionBreastConvervingSurgerySameExcisionNotDone}
                        datePropertyPath="malignantLesionBreastConvervingSurgerySameExcisionDate"
                        notDonePropertyPath="malignantLesionBreastConvervingSurgerySameExcisionNotDone"
                    />
                    <DateAndCheckboxField 
                        label={
                            <Typography variant="body1">
                                Breast conserving surgery separate incision to index
                            </Typography>
                        }
                        condition={malignantLesionBreastConvervingSurgerySeparateExcisionNotDone}
                        datePropertyPath="malignantLesionBreastConvervingSurgerySeparateExcisionDate"
                        notDonePropertyPath="malignantLesionBreastConvervingSurgerySeparateExcisionNotDone"
                    />
                    <DateAndCheckboxField 
                        label={
                            <Typography variant="body1">
                                Breast conserving surgery contralateral
                            </Typography>
                        }
                        condition={malignantLesionBreastConvervingSurgeryContralateralNotDone}
                        datePropertyPath="malignantLesionBreastConvervingSurgeryContralateralDate"
                        notDonePropertyPath="malignantLesionBreastConvervingSurgeryContralateralNotDone"
                    />
                    <DateAndCheckboxField 
                        label={
                            <Typography variant="body1">
                                BCS margin re-excision 1
                            </Typography>
                        }
                        condition={malignantLesionBcsMarginReExcision1NotDone}
                        datePropertyPath="malignantLesionBcsMarginReExcision1Date"
                        notDonePropertyPath="malignantLesionBcsMarginReExcision1NotDone"
                    />
                    <DateAndCheckboxField 
                        label={
                            <Typography variant="body1">
                                BCS margin re-excision 2
                            </Typography>
                        }
                        condition={malignantLesionBcsMarginReExcision2NotDone}
                        datePropertyPath="malignantLesionBcsMarginReExcision2Date"
                        notDonePropertyPath="malignantLesionBcsMarginReExcision2NotDone"
                    />
                    <DateAndCheckboxField 
                        label={
                            <Typography variant="body1">
                                BCS margin re-excision 1 contralateral
                            </Typography>
                        }
                        condition={malignantLesionBcsMarginReExcision1ContralateralNotDone}
                        datePropertyPath="malignantLesionBcsMarginReExcision1ContralateralDate"
                        notDonePropertyPath="malignantLesionBcsMarginReExcision1ContralateralNotDone"
                    />
                    <DateAndCheckboxField 
                        label={
                            <Typography variant="body1">
                                BCS margin re-excision 2 contralateral
                            </Typography>
                        }
                        condition={malignantLesionBcsMarginReExcision2ContralateralNotDone}
                        datePropertyPath="malignantLesionBcsMarginReExcision2ContralateralDate"
                        notDonePropertyPath="malignantLesionBcsMarginReExcision2ContralateralNotDone"
                    />
                    <DateAndCheckboxField 
                        label={
                            <Typography variant="body1">
                                Unilateral Mastectomy
                            </Typography>
                        }
                        condition={malignantLesionUnilateralMastectomyNotDone}
                        datePropertyPath="malignantLesionUnilateralMastectomyDate"
                        notDonePropertyPath="malignantLesionUnilateralMastectomyNotDone"
                    />
                    <DateAndCheckboxField 
                        label={
                            <Typography variant="body1">
                                Bilateral Mastectomy
                            </Typography>
                        }
                        condition={malignantLesionBilateralMastectomyNotDone}
                        datePropertyPath="malignantLesionBilateralMastectomyDate"
                        notDonePropertyPath="malignantLesionBilateralMastectomyNotDone"
                    />
                </CrfCondition>
                

                <GroupedFields>
                    <StyledSubheading
                        variant="h2"
                    >
                        Sentinel Node Biopsy
                    </StyledSubheading>
                </GroupedFields>

                <DateAndCheckboxField 
                    label={
                        <Typography variant="body1">
                            Was a sentinel lymph node biopsy performed?
                        </Typography>
                    }
                    condition={sentinelLymphNodeBiopsyNotDone}
                    datePropertyPath="sentinelLymphNodeBiopsyDate"
                    notDonePropertyPath="sentinelLymphNodeBiopsyNotDone"
                />
                <StyledSubheading
                    variant="h2"
                >
                    Results of Sentinel lymph node biopsy
                </StyledSubheading>
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={sentinelLymphNodeBiopsyNotDone}
                    subscription={{ value: true }}
                >
                    <GroupedTable
                        headers={sentinelNodeBiopsyHeaders}
                    >
                        <GroupedTableRow 
                            cells={sentinelNodeBiopsyCells}
                        />
                    </GroupedTable>
                </CrfCondition>

                <StyledSubheading
                    variant="h2"
                >
                    Axillary node dissection
                </StyledSubheading>

                <DateAndCheckboxField 
                    label={
                        <Typography variant="body1">
                            Was an axillary dissection performed?
                        </Typography>
                    }
                    condition={axillaryDissectionNotDone}
                    datePropertyPath="axillaryDissectionDate"
                    notDonePropertyPath="axillaryDissectionNotDone"
                />
                
                <StyledSubheading
                    variant="h2"
                >
                    Results of Axillary node dissection
                </StyledSubheading>

                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={axillaryDissectionNotDone}
                    subscription={{ value: true }}
                >
                    <GroupedTable
                        headers={axillaryNodeDissectionHeaders}
                    >
                        <GroupedTableRow 
                            cells={axillaryNodeDissectionCells}
                        />
                    </GroupedTable>
                </CrfCondition>

            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default SurgeryForm;
